import React, { useMemo, useState } from 'react';
import { Button, Chip, CircularProgress, Dialog, DialogContent, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { catalogCompanySourceMapApi } from 'api/inventory/catalogCompanySourceMap';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import {
    mapCompanySourcesToSelectOptions,
    useCompanySourceOptionsQuery,
} from 'shared/hooks/inventory/useCompanySourceOptionsQuery';

function CompanySourcesCell({ entityType, onRefresh, row, setNotification }) {
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [selectedToAdd, setSelectedToAdd] = useState([]);
    const [pendingRemove, setPendingRemove] = useState(null);

    const {
        data: catalogCompanySources = [],
        error: optionsError,
        isError: isOptionsError,
        isLoading: isOptionsLoading,
    } = useCompanySourceOptionsQuery();

    const companySourceOptions = useMemo(
        () => mapCompanySourcesToSelectOptions(catalogCompanySources),
        [catalogCompanySources],
    );

    const companySources = row.companySources ?? [];
    const assignedIds = new Set(companySources.map((source) => source.value));
    const availableOptions = companySourceOptions.filter((option) => !assignedIds.has(option.value));

    const getRemoveConfirmContent = (item) => ({
        title: 'Remove company source?',
        description:
            entityType === 'packaging'
                ? `Remove "${item.text}" from packaging #${row.id}?`
                : `Remove "${item.text}" from external SKU ${row.externalSku}?`,
        confirmLabel: 'Remove',
    });

    const handleAdd = async () => {
        const responses = await Promise.all(
            selectedToAdd.map((option) =>
                entityType === 'packaging'
                    ? catalogCompanySourceMapApi.addPackagingCompanySource(row.id, option.value)
                    : catalogCompanySourceMapApi.addExternalSkuCompanySource(row.externalSku, option.value),
            ),
        );

        const failedOptions = [];
        let succeededCount = 0;

        responses.forEach((response, index) => {
            if (response.status) {
                succeededCount += 1;
            } else {
                failedOptions.push(selectedToAdd[index]);
            }
        });

        if (succeededCount > 0) {
            onRefresh();
        }

        if (failedOptions.length === 0) {
            setAddModalOpen(false);
            setSelectedToAdd([]);
            setNotification({
                text: succeededCount === 1 ? 'Company source added' : `${succeededCount} company sources added`,
                level: 'success',
            });
            return;
        }

        setSelectedToAdd(failedOptions);

        const firstFailure = responses.find((response) => !response.status);
        const failureMessage = firstFailure?.msg || 'Failed to add company source';

        if (succeededCount > 0) {
            setNotification({
                text: `${succeededCount} added; ${failedOptions.length} failed (${failureMessage}). Retry the remaining sources.`,
                level: 'error',
            });
            return;
        }

        setNotification({
            text: failureMessage,
            level: 'error',
        });
    };

    const handleRemove = async (item) => {
        const response = await (entityType === 'packaging'
            ? catalogCompanySourceMapApi.removePackagingCompanySource(row.id, item.value)
            : catalogCompanySourceMapApi.removeExternalSkuCompanySource(row.externalSku, item.value));

        if (response.status) {
            onRefresh();
            setNotification({ text: 'Company source removed', level: 'success' });
            return;
        }

        setNotification({
            text: response.msg || 'Failed to remove company source',
            level: 'error',
        });
    };

    const removeConfirmContent = pendingRemove ? getRemoveConfirmContent(pendingRemove) : null;

    return (
        <>
            {removeConfirmContent && (
                <ConfirmDialog
                    open
                    title={removeConfirmContent.title}
                    description={removeConfirmContent.description}
                    confirmLabel={removeConfirmContent.confirmLabel}
                    confirmVariant="destructive"
                    onCancel={() => setPendingRemove(null)}
                    onConfirm={async () => {
                        await handleRemove(pendingRemove);
                        setPendingRemove(null);
                    }}
                />
            )}

            <Dialog open={addModalOpen} fullWidth maxWidth="sm" onClose={() => setAddModalOpen(false)}>
                <DialogContent>
                    <h4>Add company sources for this item</h4>
                    {isOptionsError && (
                        <p style={{ color: '#d32f2f', marginBottom: 16 }}>
                            {optionsError?.message || 'Failed to load company source options'}
                        </p>
                    )}
                    {isOptionsLoading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Autocomplete
                            id="CompanySourcesCell-Autocomplete"
                            multiple
                            options={availableOptions}
                            value={selectedToAdd}
                            onChange={(event, value) => setSelectedToAdd(value)}
                            getOptionLabel={(option) => option.text}
                            disabled={isOptionsError}
                            style={{ width: 500 }}
                            renderInput={(params) => (
                                <TextField {...params} variant="outlined" label="Company sources" />
                            )}
                        />
                    )}
                    <br />
                    <br />
                    <Button
                        id="CompanySourcesCell-Add"
                        variant="outlined"
                        disabled={selectedToAdd.length === 0 || isOptionsLoading || isOptionsError}
                        onClick={handleAdd}
                    >
                        Add Company Sources
                    </Button>
                </DialogContent>
            </Dialog>

            <div style={{ textAlign: 'center' }}>
                {companySources.map((item) => (
                    <Chip
                        key={item.id}
                        size="small"
                        label={item.text}
                        color="primary"
                        style={{ margin: '2px 1px' }}
                        onDelete={() => setPendingRemove(item)}
                    />
                ))}
                <Chip
                    size="small"
                    clickable={!isOptionsLoading}
                    color="secondary"
                    style={{ margin: '2px' }}
                    label={isOptionsLoading ? '…' : '+'}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (isOptionsLoading) {
                            return;
                        }
                        setSelectedToAdd([]);
                        setAddModalOpen(true);
                    }}
                />
            </div>
        </>
    );
}

/**
 * Stable DataTable cell renderer. Props are passed via column.companySourceCellProps
 * so react-table does not treat a new component type on every parent render.
 */
function CompanySourcesTableCell({ column, row }) {
    const { entityType, onRefresh, setNotification } = column.companySourceCellProps ?? {};

    return (
        <CompanySourcesCell
            row={row.original}
            entityType={entityType}
            onRefresh={onRefresh}
            setNotification={setNotification}
        />
    );
}

export { CompanySourcesCell, CompanySourcesTableCell };
